import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const RegistrationModal = ({ 
  show, 
  onHide, 
  patientEmail, 
  onRegister, 
  loading = false,
  error = null,
  success = false 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: patientEmail || '',
    password: '',
    passwordConfirmation: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email.trim()) {
      errors.email = t('registration-modal.email-required', 'E-Mail-Adresse ist erforderlich');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('registration-modal.email-invalid', 'Bitte geben Sie eine gültige E-Mail-Adresse ein');
    }
    
    if (!formData.password) {
      errors.password = t('registration-modal.password-required', 'Passwort ist erforderlich');
    } else if (formData.password.length < 6) {
      errors.password = t('registration-modal.password-too-short', 'Passwort muss mindestens 8 Zeichen lang sein');
    }
    
    if (!formData.passwordConfirmation) {
      errors.passwordConfirmation = t('registration-modal.password-confirmation-required', 'Passwort-Bestätigung ist erforderlich');
    } else if (formData.password !== formData.passwordConfirmation) {
      errors.passwordConfirmation = t('registration-modal.password-mismatch', 'Passwörter stimmen nicht überein');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onRegister(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      email: patientEmail || '',
      password: '',
      passwordConfirmation: ''
    });
    setValidationErrors({});
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('registration-modal.title', 'Konto erstellen')}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            {t('registration-modal.success', 'Registrierung erfolgreich! Sie können sich jetzt anmelden.')}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('registration-modal.email-label', 'E-Mail-Adresse')} *
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.email}
              disabled={loading}
              placeholder={t('registration-modal.email-placeholder', 'ihre.email@beispiel.com')}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.email}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>
              {t('registration-modal.password-label', 'Passwort')} *
            </Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.password}
              disabled={loading}
              placeholder={t('registration-modal.password-placeholder', 'Mindestens 6 Zeichen')}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.password}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>
              {t('registration-modal.password-confirmation-label', 'Passwort bestätigen')} *
            </Form.Label>
            <Form.Control
              type="password"
              name="passwordConfirmation"
              value={formData.passwordConfirmation}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.passwordConfirmation}
              disabled={loading}
              placeholder={t('registration-modal.password-confirmation-placeholder', 'Passwort wiederholen')}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.passwordConfirmation}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          {t('registration-modal.cancel', 'Abbrechen')}
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
          style={{ backgroundColor: 'var(--main-color)', borderColor: 'var(--main-color)' }}
        >
          {loading 
            ? t('registration-modal.registering', 'Wird erstellt...') 
            : t('registration-modal.register', 'Registrieren')
          }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegistrationModal;
